import {
  Button,
  ButtonVariants,
  IError,
  Modal,
  ModalVariants,
  TextField,
  TextFieldTypes,
  useToast,
} from '@comptia-sso/core';
import { ChangeEvent, ReactElement, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Hooks.
import {
  useAuthLogout,
  useResendConsent,
  useSendConsent,
  useSetConsentAdult,
} from 'hooks';

// Styles.
import styles from './ConsentManagementPrompt.module.scss';

interface IConsentManagementPromptProps {
  open: boolean;
  parentEmail?: string;
  consentStatus?: string;
  profileId: string;
  userEmail: string;
}

type Step = 'age' | 'guardian' | 'pending';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const CURRENT_YEAR = new Date().getFullYear();

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const ConsentManagementPrompt = ({
  open,
  parentEmail: initialParentEmail,
  consentStatus,
  profileId,
  userEmail,
}: IConsentManagementPromptProps): ReactElement => {
  const { t } = useTranslation();
  const [popToast] = useToast();
  const logout = useAuthLogout();

  const [sendConsentAsync, { isLoading: isSending }] =
    useSendConsent(profileId);
  const [resendConsentAsync, { isLoading: isResending }] =
    useResendConsent(profileId);
  const [setConsentAdultAsync, { isLoading: isSettingAdult }] =
    useSetConsentAdult(profileId);

  const isLoading = isSending || isResending || isSettingAdult;

  const statusLower = consentStatus?.toLowerCase();
  const isWaitingForConsent =
    statusLower === 'pending' || statusLower === 'revoked';

  const initialStep: Step = isWaitingForConsent ? 'pending' : 'age';

  const [step, setStep] = useState<Step>(initialStep);
  const [birthMonth, setBirthMonth] = useState<string>('');
  const [birthYear, setBirthYear] = useState<string>('');
  const [guardianEmail, setGuardianEmail] = useState<string>(
    initialParentEmail || '',
  );
  const [hasSent, setHasSent] = useState<boolean>(false);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  // Sync step when consentStatus prop changes (e.g. profile loads after mount).
  useEffect(() => {
    const status = consentStatus?.toLowerCase();
    if (status === 'pending' || status === 'expired' || status === 'revoked') {
      setStep('pending');
      if (initialParentEmail) {
        setGuardianEmail(initialParentEmail);
      }
    }
  }, [consentStatus, initialParentEmail]);

  const years = useMemo(() => {
    const result: number[] = [];
    for (let y = CURRENT_YEAR; y >= CURRENT_YEAR - 100; y--) {
      result.push(y);
    }
    return result;
  }, []);

  const isAdult = useMemo(() => {
    if (!birthMonth || !birthYear) return false;
    const monthIndex = MONTHS.indexOf(birthMonth);
    if (monthIndex === -1) return false;
    // User is 18 if the 1st of the month AFTER their birth month/year
    // is at least 18 years before today.
    const turnedEighteenDate = new Date(
      parseInt(birthYear) + 18,
      monthIndex + 1,
      1,
    );
    return new Date() >= turnedEighteenDate;
  }, [birthMonth, birthYear]);

  const handleAgeSubmit = async () => {
    if (!birthMonth || !birthYear) return;

    try {
      if (isAdult) {
        await setConsentAdultAsync();
        window.location.reload();
      } else {
        setStep('guardian');
      }
    } catch (error) {
      popToast((error as IError)?.message || t('Toast.Error.Default'));
    }
  };

  const isGuardianEmailSameAsUser =
    guardianEmail.trim().toLowerCase() === userEmail.trim().toLowerCase();

  const isGuardianEmailInvalid =
    guardianEmail.trim() !== '' && !EMAIL_REGEX.test(guardianEmail.trim());

  const handleGuardianSubmit = async () => {
    if (!guardianEmail || isGuardianEmailSameAsUser || isGuardianEmailInvalid)
      return;

    try {
      const monthIndex = (MONTHS.indexOf(birthMonth) + 1)
        .toString()
        .padStart(2, '0');
      await sendConsentAsync({
        birthMonth: monthIndex,
        birthYear: birthYear,
        parentEmail: guardianEmail,
      });
      setHasSent(true);
      setStep('pending');
    } catch (error) {
      popToast((error as IError)?.message || t('Toast.Error.Default'));
    }
  };

  const handleResend = async () => {
    if (!guardianEmail || isGuardianEmailSameAsUser || isGuardianEmailInvalid)
      return;

    try {
      const response = await resendConsentAsync({
        parentEmail: guardianEmail,
      });
      if (response.hitLimit) {
        popToast(t('ConsentManagement.ResendLimitReached'));
      } else {
        setHasSent(true);
      }
    } catch (error) {
      popToast((error as IError)?.message || t('Toast.Error.Default'));
    }
  };

  const getTitle = (): string => {
    switch (step) {
      case 'age':
        return t('ConsentManagement.Title');
      case 'guardian':
        return t('ConsentManagement.GuardianTitle');
      case 'pending':
        return t('ConsentManagement.PendingTitle');
    }
  };

  return (
    <Modal
      disableClose
      open={open}
      title={getTitle()}
      variant={ModalVariants.Small}
    >
      {step === 'age' && (
        <div className={styles.content}>
          <p>{t('ConsentManagement.AgeDescription')}</p>
          <div className={styles.row}>
            <TextField
              label={t('ConsentManagement.MonthLabel')}
              onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                setBirthMonth(e.target.value)
              }
              select
              value={birthMonth}
            >
              {MONTHS.map((month) => (
                <option key={month} value={month}>
                  {t(`ConsentManagement.Months.${month}`)}
                </option>
              ))}
            </TextField>
            <TextField
              label={t('ConsentManagement.YearLabel')}
              onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                setBirthYear(e.target.value)
              }
              select
              value={birthYear}
            >
              {years.map((year) => (
                <option key={year} value={year.toString()}>
                  {year}
                </option>
              ))}
            </TextField>
          </div>
          <Button
            className={styles.fullWidth}
            disabled={isLoading || !birthMonth || !birthYear}
            onClick={handleAgeSubmit}
          >
            {t('ConsentManagement.SubmitAge')}
          </Button>
        </div>
      )}

      {step === 'guardian' && (
        <div className={styles.content}>
          <p>{t('ConsentManagement.GuardianDescription')}</p>
          <TextField
            error={
              isGuardianEmailSameAsUser
                ? t('ConsentManagement.SameEmailError')
                : isGuardianEmailInvalid
                ? t('ConsentManagement.InvalidEmailError')
                : undefined
            }
            label={t('ConsentManagement.GuardianEmailLabel')}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setGuardianEmail(e.target.value)
            }
            type={TextFieldTypes.Email}
            value={guardianEmail}
          />
          <Button
            className={styles.fullWidth}
            disabled={
              isLoading ||
              !guardianEmail ||
              isGuardianEmailSameAsUser ||
              isGuardianEmailInvalid
            }
            onClick={handleGuardianSubmit}
          >
            {t('ConsentManagement.SubmitGuardian')}
          </Button>
        </div>
      )}

      {step === 'pending' && (
        <div className={styles.content}>
          {hasSent && <p>{t('ConsentManagement.SentConfirmation')}</p>}
          {!hasSent && <p>{t('ConsentManagement.PendingDescription')}</p>}
          {!hasSent && (
            <>
              <TextField
                error={
                  isGuardianEmailSameAsUser
                    ? t('ConsentManagement.SameEmailError')
                    : isGuardianEmailInvalid
                    ? t('ConsentManagement.InvalidEmailError')
                    : undefined
                }
                label={t('ConsentManagement.GuardianEmailLabel')}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setGuardianEmail(e.target.value)
                }
                type={TextFieldTypes.Email}
                value={guardianEmail}
              />
              <Button
                className={styles.fullWidth}
                disabled={
                  isLoading ||
                  !guardianEmail ||
                  isGuardianEmailSameAsUser ||
                  isGuardianEmailInvalid
                }
                onClick={handleResend}
              >
                {t('ConsentManagement.ResendButton')}
              </Button>
            </>
          )}
          <Button
            className={styles.fullWidth}
            disabled={isRefreshing}
            onClick={() => {
              setIsRefreshing(true);
              window.location.reload();
            }}
            variant={ButtonVariants.Secondary}
          >
            {t('ConsentManagement.RefreshStatus')}
          </Button>
          <Button
            className={styles.fullWidth}
            onClick={() => logout()}
            variant={ButtonVariants.Text}
          >
            {t('ConsentManagement.SignOut')}
          </Button>
        </div>
      )}
    </Modal>
  );
};
